import React, { useState } from "react";
import { X } from "lucide-react";

// --- Field (matches the OTH admin visual language) ---
export const SCField = ({ label, value, onChange, type = "text", testId, hint, required, disabled, options, placeholder }) => (
    <label className="block mb-4">
        <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-1.5">
            {label}{required && <span className="text-red-500 ml-1">*</span>}
        </div>
        {type === "textarea" ? (
            <textarea
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder={placeholder}
                className="w-full bg-white border border-oth-line/70 rounded-2xl px-4 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors min-h-[80px] disabled:bg-oth-bg-2 disabled:text-oth-ink3"
                data-testid={testId}
            />
        ) : type === "select" ? (
            <select
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="w-full bg-white border border-oth-line/70 rounded-2xl px-4 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors disabled:bg-oth-bg-2 disabled:text-oth-ink3"
                data-testid={testId}
            >
                {(options || []).map((o) => (
                    <option key={typeof o === "object" ? o.value : o} value={typeof o === "object" ? o.value : o}>
                        {typeof o === "object" ? o.label : o}
                    </option>
                ))}
            </select>
        ) : (
            <input
                type={type}
                value={value ?? ""}
                onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                disabled={disabled}
                placeholder={placeholder}
                className="w-full bg-white border border-oth-line/70 rounded-2xl px-4 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors disabled:bg-oth-bg-2 disabled:text-oth-ink3"
                data-testid={testId}
            />
        )}
        {hint && <div className="text-[11px] text-oth-ink3 mt-1.5 font-body">{hint}</div>}
    </label>
);

// --- Modal (glass) ---
export const SCModal = ({ open, title, onClose, children, footer, testId, maxWidth = 640 }) => {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-[100] flex items-start md:items-center justify-center px-3 py-6 md:p-8 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
            data-testid={`${testId}-backdrop`}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full bg-white rounded-3xl border border-oth-line/70 shadow-2xl max-h-[92vh] overflow-hidden flex flex-col"
                style={{ maxWidth }}
                data-testid={testId}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-oth-line/70">
                    <div className="font-display text-lg font-semibold tracking-tight">{title}</div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full hover:bg-oth-bg-2 flex items-center justify-center transition-colors"
                        aria-label="Close"
                        data-testid={`${testId}-close`}
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-5 thin-scroll">{children}</div>
                {footer && <div className="px-6 py-4 border-t border-oth-line/70 bg-oth-bg-2/40 flex items-center justify-end gap-3">{footer}</div>}
            </div>
        </div>
    );
};

// --- Confirm dialog (inline) ---
export const useConfirm = () => {
    const [state, setState] = useState({ open: false, message: "", resolve: null });
    const confirm = (message) =>
        new Promise((resolve) => setState({ open: true, message, resolve }));
    const dialog = state.open ? (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-3xl border border-oth-line/70 shadow-2xl w-full max-w-[420px] p-6">
                <div className="font-display text-lg font-semibold tracking-tight mb-2">Are you sure?</div>
                <div className="text-sm font-body text-oth-ink2 mb-6">{state.message}</div>
                <div className="flex items-center justify-end gap-3">
                    <button
                        onClick={() => { state.resolve(false); setState({ open: false, message: "", resolve: null }); }}
                        className="px-4 py-2 rounded-full text-sm font-body text-oth-ink2 hover:bg-oth-bg-2"
                        data-testid="sc-confirm-cancel"
                    >Cancel</button>
                    <button
                        onClick={() => { state.resolve(true); setState({ open: false, message: "", resolve: null }); }}
                        className="px-4 py-2 rounded-full text-sm font-body bg-red-600 text-white hover:bg-red-700"
                        data-testid="sc-confirm-ok"
                    >Yes, proceed</button>
                </div>
            </div>
        </div>
    ) : null;
    return { confirm, dialog };
};

// --- Empty state ---
export const SCEmpty = ({ message, cta, testId }) => (
    <div className="rounded-2xl border border-dashed border-oth-line/70 p-8 text-center" data-testid={testId}>
        <div className="text-sm font-body text-oth-ink3">{message}</div>
        {cta && <div className="mt-4">{cta}</div>}
    </div>
);

// --- Section header ---
export const SCSection = ({ title, subtitle, right, testId }) => (
    <div className="flex items-end justify-between gap-4 mb-5" data-testid={testId}>
        <div>
            <div className="font-display text-xl md:text-2xl font-semibold tracking-tight text-oth-ink">{title}</div>
            {subtitle && <div className="mt-1 text-sm font-body text-oth-ink3">{subtitle}</div>}
        </div>
        {right && <div className="shrink-0">{right}</div>}
    </div>
);

export const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
};

export const fmtDateShort = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return iso;
    }
};
